import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import "./AgendamentoForm.css";

const AgendamentoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState("");
  const [hora, setHora] = useState("");
  const [clienteNome, setClienteNome] = useState("");
  const [telefoneCelular, setTelefoneCelular] = useState("");
  const [funcionarioId, setFuncionarioId] = useState("");
  const [servico, setServico] = useState("");

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [funcionarios, setFuncionarios] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let ativo = true;

    const fetchDados = async () => {
      setLoading(true);
      setErro("");

      try {
        await api.get("/verificar-autenticacao", {
          withCredentials: true,
        });

        const [funcionariosResponse, servicosResponse] =
          await Promise.all([
            api.get("/funcionarios", { withCredentials: true }),
            api.get("/servicos", { withCredentials: true }),
          ]);

        if (!ativo) return;

        setFuncionarios(funcionariosResponse.data);
        setServicos(servicosResponse.data);

        if (id) {
          const agendamentoResponse = await api.get(
            `/agendamentos/${id}`,
            { withCredentials: true }
          );

          if (!ativo) return;

          const agendamento = agendamentoResponse.data;

          setData(agendamento.data || "");
          setHora(
            agendamento.hora
              ? String(agendamento.hora).slice(0, 5)
              : ""
          );
          setClienteNome(agendamento.cliente_nome || "");
          setTelefoneCelular(agendamento.telefone_celular || "");
          setFuncionarioId(
            agendamento.id_funcionario != null
              ? String(agendamento.id_funcionario)
              : ""
          );
          setServico(
            agendamento.id_servico != null
              ? String(agendamento.id_servico)
              : ""
          );
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error);

        if (!ativo) return;

        if (error.response?.status === 401) {
          setErro("Sua sessão expirou. Faça login novamente.");
          navigate("/login");
          return;
        }

        setErro(
          error.response?.data?.error ||
          error.response?.data?.erro ||
          "Não foi possível carregar os dados. Tente novamente."
        );
      } finally {
        if (ativo) setLoading(false);
      }
    };

    fetchDados();

    return () => {
      ativo = false;
    };
  }, [id, navigate]);

  const validarTelefone = (telefone) => {
    const padraoTelefone = /^\(\d{2}\)\s9\d{4}-\d{4}$/;
    return padraoTelefone.test(telefone);
  };

  const handleTelefoneChange = (e) => {
    let telefone = e.target.value.replace(/\D/g, "").slice(0, 11);

    if (telefone.length > 7) {
      telefone = `(${telefone.slice(0, 2)}) ${telefone.slice(
        2,
        7
      )}-${telefone.slice(7)}`;
    } else if (telefone.length > 2) {
      telefone = `(${telefone.slice(0, 2)}) ${telefone.slice(2)}`;
    } else if (telefone.length > 0) {
      telefone = `(${telefone}`;
    }

    setTelefoneCelular(telefone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (
      !data ||
      !hora ||
      !clienteNome.trim() ||
      !telefoneCelular ||
      !funcionarioId ||
      !servico
    ) {
      setErro("Preencha todos os campos obrigatórios.");
      return;
    }

    if (!validarTelefone(telefoneCelular)) {
      setErro(
        "Informe um celular válido no formato (XX) 9XXXX-XXXX."
      );
      return;
    }

    const agendamentoData = {
      data,
      hora,
      cliente_nome: clienteNome.trim(),
      telefone_celular: telefoneCelular,
      id_funcionario: funcionarioId,
      id_servico: servico,
    };

    setSalvando(true);

    try {
      if (id) {
        await api.put(
          `/agendamentos/${id}`,
          agendamentoData,
          { withCredentials: true }
        );
        setSucesso("Agendamento atualizado com sucesso!");
      } else {
        await api.post(
          "/agendamentos",
          agendamentoData,
          { withCredentials: true }
        );
        setSucesso("Agendamento criado com sucesso!");
      }

      navigate("/dashboard");
    } catch (error) {
      console.error("Erro ao criar ou atualizar agendamento:", error);

      if (error.response?.status === 401) {
        setErro("Sua sessão expirou. Faça login novamente.");
        navigate("/login");
        return;
      }

      const mensagem =
        error.response?.data?.error ||
        error.response?.data?.erro ||
        error.response?.data?.mensagem;

      setErro(
        mensagem || "Não foi possível salvar o agendamento. Tente novamente."
      );
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <div className="agendamento-loading">
        <span className="agendamento-spinner" />
        <p>Preparando seu formulário...</p>
      </div>
    );
  }

  return (
    <main className="agendamento-form-page">
      <section className="agendamento-form-card">
        <header className="agendamento-form-header">
          <div className="agendamento-form-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="5" width="18" height="16" rx="3" />
              <path d="M16 3v4M8 3v4M3 10h18M12 14v4M10 16h4" />
            </svg>
          </div>

          <div>
            <span className="agendamento-form-eyebrow">
              GESTÃO DE AGENDA
            </span>
            <h1>
              {id ? "Editar agendamento" : "Novo agendamento"}
            </h1>
            <p>
              {id
                ? "Atualize os dados do atendimento."
                : "Preencha os dados para registrar um atendimento."}
            </p>
          </div>
        </header>

        {erro && (
          <div className="agendamento-feedback agendamento-feedback-error" role="alert">
            <span className="agendamento-feedback-icon">!</span>
            <p>{erro}</p>
          </div>
        )}

        {sucesso && (
          <div className="agendamento-feedback agendamento-feedback-success" role="status">
            <span className="agendamento-feedback-icon">✓</span>
            <p>{sucesso}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="agendamento-form">
          <div className="agendamento-section-title">
            <span className="agendamento-section-number">01</span>
            <div>
              <h2>Informações do atendimento</h2>
              <p>Defina a data, o horário e os responsáveis.</p>
            </div>
          </div>

          <div className="agendamento-form-grid">
            <div className="agendamento-field">
              <label htmlFor="agendamento-data">Data *</label>
              <input
                id="agendamento-data"
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                required
              />
            </div>

            <div className="agendamento-field">
              <label htmlFor="agendamento-hora">Horário *</label>
              <input
                id="agendamento-hora"
                type="time"
                value={hora}
                onChange={(e) => setHora(e.target.value)}
                required
              />
            </div>

            <div className="agendamento-field agendamento-field-full">
              <label htmlFor="agendamento-profissional">Profissional *</label>
              <select
                id="agendamento-profissional"
                value={funcionarioId}
                onChange={(e) => setFuncionarioId(e.target.value)}
                required
              >
                <option value="">Selecione o profissional</option>
                {funcionarios.map((funcionario) => (
                  <option key={funcionario.id} value={funcionario.id}>
                    {funcionario.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="agendamento-field agendamento-field-full">
              <label htmlFor="agendamento-servico">Serviço *</label>
              <select
                id="agendamento-servico"
                value={servico}
                onChange={(e) => setServico(e.target.value)}
                required
              >
                <option value="">Selecione o serviço</option>
                {servicos.map((servicoItem) => (
                  <option key={servicoItem.id} value={servicoItem.id}>
                    {servicoItem.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="agendamento-form-divider" />

          <div className="agendamento-section-title">
            <span className="agendamento-section-number">02</span>
            <div>
              <h2>Dados do cliente</h2>
              <p>Informe os dados para identificar o atendimento.</p>
            </div>
          </div>

          <div className="agendamento-form-grid">
            <div className="agendamento-field agendamento-field-full">
              <label htmlFor="agendamento-cliente">Nome do cliente *</label>
              <input
                id="agendamento-cliente"
                type="text"
                value={clienteNome}
                onChange={(e) => setClienteNome(e.target.value)}
                placeholder="Ex.: João da Silva"
                autoComplete="name"
                maxLength={150}
                required
              />
            </div>

            <div className="agendamento-field agendamento-field-full">
              <label htmlFor="agendamento-telefone">Celular do cliente *</label>
              <input
                id="agendamento-telefone"
                type="tel"
                inputMode="numeric"
                value={telefoneCelular}
                onChange={handleTelefoneChange}
                placeholder="(00) 90000-0000"
                autoComplete="tel"
                maxLength={15}
                required
              />
              <span className="agendamento-field-hint">
                Inclua o DDD e os 9 dígitos do celular.
              </span>
            </div>
          </div>

          <footer className="agendamento-form-actions">
            <button
              type="button"
              className="agendamento-btn agendamento-btn-secondary"
              onClick={() => navigate("/dashboard")}
              disabled={salvando}
            >
              Voltar
            </button>

            <button
              type="submit"
              className="agendamento-btn agendamento-btn-primary"
              disabled={salvando}
            >
              {salvando ? (
                <>
                  <span className="agendamento-button-spinner" />
                  Salvando...
                </>
              ) : (
                <>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  {id ? "Salvar alterações" : "Confirmar agendamento"}
                </>
              )}
            </button>
          </footer>
        </form>
      </section>

      <p className="agendamento-form-note">
        <span>🔒</span> Seus dados são tratados de forma segura.
      </p>
    </main>
  );
};

export default AgendamentoForm;